import type { UserState } from 'store/state';
import type { UserRelations } from 'types/processInfo.types';
import { addErrorMessage, type FormValues } from './form.helpers';
import { isCompany, isKennitala } from './kennitala';
import { isEmail, isPhoneNumber } from './string.helpers';

type JwtPayload = {
	email?: string;
	exp?: number;
	given_name?: string;
	phone_number?: string;
	sub?: string;
} & Record<string, unknown>;

// Decodes the payload without signature verification; verification happens server-side.
export function decodeJwt(token: string): JwtPayload {
	const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
	const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
	return JSON.parse(new TextDecoder().decode(bytes));
}

export function isLogged(user: UserState) {
	const { email, exp, kennitala, phone, token } = user;
	return !!(
		phone &&
		isEmail(email) &&
		isKennitala(kennitala) &&
		tokenData(token) &&
		Number.isInteger(exp) &&
		exp > Date.now() / 1000
	);
}

export function tokenData(token: string) {
	try {
		return decodeJwt(token);
	} catch (ex) {
		console.info(ex);
		return false;
	}
}

const ALLOWED_ROLES = ['IPA-Admin', 'Curity-Market-Developers'];

export function hasAllowedRole(token: string) {
	const data = tokenData(token);
	if (!data) {
		return false;
	}
	const { roles } = data;
	if (Array.isArray(roles)) {
		return ALLOWED_ROLES.some((role) => roles.includes(role));
	}
	return typeof roles === 'string' && ALLOWED_ROLES.some((role) => roles.includes(role));
}

export function expirationTime() {
	const date = new Date();
	date.setFullYear(date.getFullYear() + 1);
	return Math.round(date.getTime() / 1000);
}

export function validateAuthForm(formValues: FormValues) {
	const errors = {};
	const phoneOrKennitala = `${formValues?.phone}`;
	if (!isPhoneNumber(phoneOrKennitala) && !isKennitala(phoneOrKennitala)) {
		addErrorMessage(errors, 'phone', 'INVALID_PHONE_OR_KENNITALA');
	}
	if (!isEmail(`${formValues?.email}`)) {
		addErrorMessage(errors, 'email', 'INVALID_EMAIL');
	}
	return errors;
}

export function validateAuthEmployeeForm(formValues: FormValues) {
	const errors = {};
	const companyKennitala = `${formValues?.companyKennitala ?? ''}`;
	if (!isPhoneNumber(`${formValues?.phone ?? ''}`)) {
		addErrorMessage(errors, 'phone', 'INVALID_PHONE_NUMBER');
	}
	if (!isEmail(`${formValues?.email ?? ''}`)) {
		addErrorMessage(errors, 'email', 'INVALID_EMAIL');
	}
	if (!isKennitala(`${formValues?.kennitala ?? ''}`)) {
		addErrorMessage(errors, 'kennitala', 'INVALID_KENNITALA');
	}
	if (formValues?.isManualSignCompany) {
		if (isKennitala(companyKennitala)) {
			if (!isCompany(companyKennitala)) {
				addErrorMessage(errors, 'companyKennitala', 'INVALID_COMPANY_KENNITALA');
			}
		} else {
			addErrorMessage(errors, 'companyKennitala', 'INVALID_KENNITALA');
		}
	}
	return errors;
}

export function getDefaultRelations(): UserRelations {
	return {
		children: [],
		companies: [],
		person: {
			disabledButton: false,
			disabledCard: false,
			kennitala: '',
			name: '',
			processInfo: null,
			roles: ''
		}
	};
}
